import { ResolvedThemeName } from "./theme";

/**
 * localStorage key used to persist the visitor's manually-chosen theme
 * preference ("light" | "dark"), read back on subsequent visits so a
 * visitor's explicit choice is respected ahead of both the org's
 * `brandThemeMode` default and the OS-level `prefers-color-scheme`.
 *
 * Shared between `BrandStyleTag` (CSS var injection) and
 * `useActiveBrandTheme` (the resolved theme used for
 * ThemeProvider/styled-components), so both read/write the exact same
 * stored value rather than duplicating the key or the read/write logic.
 */
export const VISITOR_THEME_STORAGE_KEY = "ap-visitor-theme-preference";

/**
 * Reads the visitor's manually-stored theme preference from localStorage,
 * if any. Returns `null` when nothing is stored, or when storage is
 * unavailable (private browsing, disabled cookies, SSR, etc.).
 */
export function readVisitorThemePreference(): ResolvedThemeName | null {
  try {
    const stored = window.localStorage.getItem(VISITOR_THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Persists the visitor's manually-chosen theme preference to localStorage,
 * so it's respected on subsequent visits. Exported so components that
 * expose a manual theme toggle can call this alongside switching which
 * Theme they render.
 */
export function setVisitorThemePreference(theme: ResolvedThemeName): void {
  try {
    window.localStorage.setItem(VISITOR_THEME_STORAGE_KEY, theme);
  } catch {
    // Ignore storage failures — the in-memory switch still takes effect
    // for the current session even if it can't be persisted.
  }
}

/**
 * Reads the visitor's OS-level `prefers-color-scheme` setting. Returns
 * `null` in environments without `window.matchMedia` (SSR, some test
 * environments).
 */
export function getSystemPreference(): ResolvedThemeName | null {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}
